import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const ResetPage = ({ UserService }) => {
    const { t } = useTranslation();

    const [randcodehash, setRandcodehash] = useState(null);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [tokenValid, setTokenValid] = useState(null); // Track token validity, initially null (undetermined)
    const [passwordReset, setPasswordReset] = useState(false);
    const [loading, setLoading] = useState(true); // Loading state for token verification

    useEffect(() => {
        let fullUrl = window.location.href;
        const randCode = fullUrl.substring(fullUrl.lastIndexOf("/") + 1); // Extract randcodehash and remove leading slash
        // console.log(randCode);

        if (randCode) {
            setRandcodehash(randCode);

            // Validate the token as soon as the component mounts
            const checkTokenValidity = async () => {
                try {
                    setLoading(true); // Set loading to true before checking the token
                    const isCodeValid =
                        await UserService.checkCodePass(randCode);
                    // console.log(isCodeValid);
                    setLoading(false); // Set loading to false after token verification

                    setTimeout(() => {
                        // Redirect to home if the token is invalid
                        setLoading(false);
                    }, 3000); // Redirect after 2 seconds for a better user experience
                    if (!isCodeValid.status) {
                        setTokenValid(false);
                        setPasswordError("Invalid password reset token.");
                        setTimeout(() => {
                            // Redirect to home if the token is invalid
                            window.location.href = "/";
                        }, 2000); // Redirect after 2 seconds for a better user experience
                    } else {
                        setTokenValid(true); // Token is valid, so we proceed
                    }
                } catch (error) {
                    console.error("Error checking token validity:", error);
                    setLoading(false); // Set loading to false after the error
                    setTokenValid(false);
                    setPasswordError(
                        "There was an error verifying your token. Please try again.",
                    );
                    setTimeout(() => {
                        // Redirect to home if there's an error
                        window.location.href = "/";
                    }, 2000);
                }
            };

            checkTokenValidity();
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            setPasswordError("Passwords do not match");
            return;
        }

        try {
            const changeResult = await UserService.changePass(
                randcodehash,
                newPassword,
            );

            if (changeResult.status) {
                setPasswordReset(true);
                setNewPassword("");
                setConfirmPassword("");
                setTimeout(() => {
                    // Redirect to home if there's an error
                    window.location.href = "/";
                }, 2000);
            } else {
                setPasswordError("Error when changing password");
            }
        } catch (error) {
            console.error("Error:", error);
            setPasswordError("There was an error. Please try again.");
        }
    };

    // If the token is invalid or still being verified, show loading or error message
    if (loading) {
        return (
            <div className="no-content-search">
                <box-icon name="error-alt" size="lg"></box-icon>
                <h3>Verifying your token...</h3>
            </div>
        );
    }

    if (!tokenValid) {
        return (
            <>
                <p className="no-content-search">
                    <box-icon name="loader" size="lg"></box-icon>
                    <h3>{passwordError}</h3>
                </p>
            </>
        );
    }

    return (
        <section className="container forms">
            <div className="form login">
                <div className="form-content">
                    <form onSubmit={handleSubmit}>
                        <div className="form-link">
                            <span className="forgot-pass mt-5 d-block">
                                Forgot password
                            </span>
                        </div>
                        <div className="field input-field">
                            <input
                                type="text"
                                placeholder="New Password"
                                className="input"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                            />
                        </div>
                        <div className="field input-field">
                            <input
                                type="password"
                                placeholder="Confirm Password"
                                className="input"
                                value={confirmPassword}
                                onChange={(e) =>
                                    setConfirmPassword(e.target.value)
                                }
                                required
                            />
                        </div>
                        {passwordError && (
                            <div className="no-content-search">
                                <box-icon name="error-alt" size="lg"></box-icon>
                                <h3>{passwordError}</h3>
                            </div>
                        )}

                        <div className="field button-field">
                            <button type="submit">Reset Password</button>
                        </div>
                        {passwordReset && (
                            <div className="success-message">
                                <p className="no-content-search">
                                    <box-icon
                                        name="info-circle"
                                        size="lg"
                                    ></box-icon>
                                    <h3>Password successfully reset!</h3>
                                    <p>
                                        You can now log in with your new
                                        password.
                                    </p>
                                </p>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </section>
    );
};

export default ResetPage;
